import type { ReactNode } from 'react';

export interface ButtonProps {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly type?: 'button' | 'submit';
  readonly disabled?: boolean;
  /**
   * `primary` is the filled action; `secondary` is the quiet one beside it.
   *
   * `outline` is a THIRD treatment the design actually draws, not a synonym for `secondary`: the I-9 frame
   * [`12021:41105`] gives its declining action a brand-coloured 1.5px stroke and a brand-coloured label,
   * where every earlier frame draws the same slot with a neutral border and body-coloured text. Two
   * secondaries in one design system is a reconciliation owed by the designer; until then, collapsing them
   * would put the wrong one on one of the two screens.
   */
  readonly variant?: 'primary' | 'secondary' | 'outline';
  readonly fullWidth?: boolean;
}

/**
 * The design system's Button [Figma · component 1818:1444].
 *
 * Values read from the frame: `--radius-md` (12px, NOT a pill), `px-6 py-4`, 16px semibold, 20px line.
 *
 * ⚠️ **Letter spacing is 0.4px, not 2.5px.** The *Actions/Button/L Semi-Bold* style reports
 * `letterSpacing: 2.5`, and that number is a **percentage of the font size** — 2.5% of 16px = 0.4px, which
 * is what the frame actually renders. An earlier version of this file took the 2.5 as pixels and every
 * button label on the kiosk came out spaced like a logo. The same trap applies to the Chip style's `1.5`
 * (1.5% of 14px = 0.21px). Read the frame's rendered value, never the style sheet's raw number.
 */
export function Button({
  children,
  onClick,
  type = 'button',
  disabled = false,
  variant = 'primary',
  fullWidth = false,
}: ButtonProps) {
  const TONE = {
    primary: 'bg-[var(--primary)] text-[var(--primary-foreground)]',
    // 1.5px, and `--foreground` rather than `--primary`: those frames draw the secondary action in body
    // text, so the two buttons differ by weight of fill, not by colour of label.
    secondary:
      'ring-[1.5px] ring-inset ring-[color:var(--border-subtle)] bg-[var(--card)] text-[var(--foreground)]',
    // The I-9 frame's declining action: transparent, brand stroke, brand label. Measured 4.69:1 on --card.
    outline: 'ring-[1.5px] ring-inset ring-[color:var(--primary)] text-[var(--primary)]',
  } as const;
  /*
   * The outlined tones draw their edge with an INSET RING, not a border.
   *
   * This is the component the claim is TRUE of: it has no fixed height, so a border added to its box.
   * Measured — 54 tall with `border-[1.5px]`, 52 with `ring-[1.5px]`, against a drawn 52. The gate saw it
   * as `Actions dh+3` on No jobs, the one screen whose footer holds a bordered button alone.
   *
   * `Field` and `Select` follow the same idiom for consistency, but there the change is dimensionally
   * inert: both carry `h-10`, so their border was already inside the box.
   */
  const tone = TONE[variant];

  return (
    <button
      className={`kiosk-focusable flex min-h-touch items-center justify-center gap-2 rounded-[var(--radius-md)] px-6 py-4 text-base font-semibold leading-5 tracking-[0.4px] disabled:opacity-50 ${tone} ${fullWidth ? 'w-full' : ''}`}
      disabled={disabled}
      onClick={onClick}
      type={type}
    >
      {children}
    </button>
  );
}
